var _jsxFileName = "/codebuild/output/src1174814487/src/bitbucket.org/newyuinc/adc-polaris-web/src/components/register/CreateNewUser.tsx";
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { InformationalScreen, LoadingModal } from '@adc/parallax-component-library';
import { ApiStatus, ErrorCode } from 'Constants';
import { useFormik } from 'formik';
import { useAccount, useApiError, useAuth, useClickContinueEmailVerificationButton, useResendVerificationEmail } from 'Hooks';
import CheckYourEmailComponents from 'Components/register/CheckYourEmail/components';
import { Footer, MainContainer, TopHeaderBackArrow } from 'Components/utility';
import { AUTH_SESSION } from 'Reducers/auth/types';
import { notifyApp } from 'Utilities/appCommunicator';
import { isBypassPhoneNumberEnabled, validateFrenchZipCode } from 'Utilities/dataHelpers';
import { _config } from 'Utilities/envConfig';
import i18n from 'Utilities/i18n';
import mediator from 'Utilities/mediator';
import { store } from 'Utilities/store';
import { listenForBypassPhone } from 'Services/appBridge';
import { signIn } from 'Services/auth';
import { registerPatient } from 'Services/patient';
import { ContentContainer, createYupSchema, initialState } from './helpers';
import { RegisterAccountForm, RegisterAdultAccount, RegisterConfirmCountry, RegisterMinorAccount, RegisterMinorAccountInfo, RegisterMinorAccountIntro } from './steps';
const PageIndexes = {
  REGISTER_CONFIRM_COUNTRY: 0,
  REGISTER_ACCOUNT_FORM: 1,
  REGISTER_ADULT_ACCOUNT: 2,
  REGISTER_MINOR_ACCOUNT_INTRO: 3,
  REGISTER_MINOR_ACCOUNT_INFO: 4,
  REGISTER_MINOR_ACCOUNT: 5,
  REGISTER_CHECK_EMAIL: 6
};
const mapStateToProps = ({
  auth: {
    account_id
  },
  env: {
    deviceId,
    installationId,
    osType,
    osVersion,
    app,
    appVersion,
    country,
    phoneLanguage,
    appHash
  },
  config: {
    validation,
    minAge,
    passwordRules,
    supportedCountries,
    countries
  }
}) => {
  return {
    account_id,
    deviceId,
    installationId,
    osType,
    osVersion,
    app,
    appVersion,
    validation,
    minAge,
    firstNameLimit: validation.patient.firstNameMaxLength,
    lastNameLimit: validation.patient.lastNameMaxLength,
    country,
    phoneLanguage,
    appHash,
    supportedCountries,
    countries,
    passwordRules
  };
};
const connector = connect(mapStateToProps);
const CreateNewUser = ({
  account_id,
  minAge,
  app,
  appVersion,
  country,
  deviceId,
  installationId,
  firstNameLimit,
  lastNameLimit,
  osType,
  osVersion,
  phoneLanguage,
  appHash,
  countries,
  supportedCountries,
  passwordRules
}) => {
  var _formController$value;
  const isLLU = app === 'llu';
  const [loading, setLoading] = useState(false);
  const [meetsPasswordRequirements, setMeetsPasswordRequirements] = useState(false);
  const [currentPageIndex, setCurrentPageIndex] = useState(isLLU ? PageIndexes.REGISTER_CONFIRM_COUNTRY : PageIndexes.REGISTER_ACCOUNT_FORM);
  const {
    showApiErrorModal,
    showNetworkErrorModal
  } = useApiError();
  const {
    showEmailExistsMessage,
    showExistingProUserError,
    showPasswordValidationError,
    showMinorRestrictionMessage,
    validateEntriesLengthAndDisplayError,
    validateIsMinor
  } = useAccount();
  const {
    handleAndroidIntegrityError,
    showAppUpgradeError
  } = useAuth();
  const validationSchema = useMemo(() => createYupSchema(currentPageIndex, app), [currentPageIndex, app]);
  const formController = useFormik({
    initialValues: {
      ...initialState,
      country: country || (isLLU ? '' : 'US')
    },
    validationSchema: validationSchema,
    validateOnBlur: true,
    onSubmit: values => handleSubmitRegister(values)
  });
  const {
    handleSubmit,
    setValues,
    setTouched,
    setErrors,
    setFieldError,
    isValid,
    isSubmitting,
    values,
    touched
  } = formController;
  const showPhoneInput = !isLLU && values.country === 'US';
  useEffect(() => {
    if (_config.environment !== 'production') {
      const cleanup = listenForBypassPhone();
      return cleanup;
    }
  }, []);
  const handleClickSignIn = useCallback(() => {
    /*
      Need to use the redirect method to replaces the current location in browser history.
      When the user clicks the back button on the login page, it should redirect the user back to the app.
    */
    mediator.publish('router:redirect', '/login');
  }, []);
  const validateNameLength = useCallback((firstName, lastName) => {
    const isValid = validateEntriesLengthAndDisplayError([{
      value: firstName,
      msgKey: 'CreateAccountAccountInfo.modals.accountInfoErrors.validationRules.firstNameMaxLength',
      count: firstNameLimit
    }, {
      value: lastName,
      msgKey: 'CreateAccountAccountInfo.modals.accountInfoErrors.validationRules.lastNameMaxLength',
      count: lastNameLimit
    }]);
    return isValid;
  }, [firstNameLimit, lastNameLimit, validateEntriesLengthAndDisplayError]);
  const handleApiRequestError = useCallback(error => {
    var _error$response;
    const data = (_error$response = error.response) !== null && _error$response !== void 0 && _error$response.data ? error.response.data : {
      details: error.code === ErrorCode.NETWORK_ERROR ? ErrorCode.NETWORK_ERROR : i18n.t('Global.modals.errorCommunicatingServer.body')
    };
    notifyApp('adc-webview:create-account', {
      status: error.status,
      data
    });
    switch (error.status) {
      case ApiStatus.CONFLICT:
        {
          var _error$response2;
          const code = (_error$response2 = error.response) === null || _error$response2 === void 0 ? void 0 : _error$response2.data.code;
          if (code === ApiStatus.FORBIDDEN_HCP_ACCOUNT) {
            showExistingProUserError();
            return;
          }
          if (code === ApiStatus.PHONE_NUMBER_ALREADY_EXISTS_CODE) {
            setFieldError('mobileNumber', i18n.t('CreateAccountAccountInfo.content.createAccountAccountInfoForm.formField.phoneNumber.errors.enterDifferentNumber'));
            return;
          }
          showEmailExistsMessage(handleClickSignIn);
          break;
        }
      case ApiStatus.BAD_REQUEST:
        {
          var _error$response3, _error$response4;
          const code = (_error$response3 = error.response) === null || _error$response3 === void 0 ? void 0 : _error$response3.data.code;
          const errors = (_error$response4 = error.response) === null || _error$response4 === void 0 ? void 0 : _error$response4.data.errors;
          const errorDetail = errors === null || errors === void 0 ? void 0 : errors[0];
          if ((errorDetail === null || errorDetail === void 0 ? void 0 : errorDetail.key) === 'phoneNumber' && errorDetail.error === 'invalidInput') {
            setFieldError('mobileNumber', i18n.t('CreateAccountAccountInfo.content.createAccountAccountInfoForm.formField.phoneNumber.errors.validPhoneNumber'));
            return;
          }
          if (code === ApiStatus.INVALID_DATA_CODE) {
            showPasswordValidationError(passwordRules, errors, values.country);
            return;
          }
          if (code === ApiStatus.ANDROID_INTEGRITY_ERROR) {
            handleAndroidIntegrityError(error);
            return;
          }
          showApiErrorModal();
          break;
        }
      case ApiStatus.UNAUTHORIZED:
        {
          var _error$response5;
          const code = (_error$response5 = error.response) === null || _error$response5 === void 0 ? void 0 : _error$response5.data.code;
          if (code === ApiStatus.ANDROID_INTEGRITY_ERROR) {
            handleAndroidIntegrityError(error);
            return;
          }

          // 401 errors should log the user out/ send them back to sign in
          mediator.publish('router:navigate', '/login');

          // TODO: Show a better auth session expired modal (this was implemented too close to the release for new strings to be added)
          showApiErrorModal();
          break;
        }
      case ApiStatus.UPGRADE_REQUIRED:
        {
          // Remove this if statement when Libre follows the same error handling
          if (app === 'llu') {
            showAppUpgradeError();
          }
          break;
        }
      default:
        error.code === ErrorCode.NETWORK_ERROR ? showNetworkErrorModal() : showApiErrorModal();
    }
  }, [app, handleAndroidIntegrityError, handleClickSignIn, passwordRules, setFieldError, showApiErrorModal, showAppUpgradeError, showEmailExistsMessage, showExistingProUserError, showNetworkErrorModal, showPasswordValidationError, values.country]);
  const signInUser = useCallback(async ({
    email,
    password,
    callback
  }) => {
    const authSession = await signIn({
      email: email,
      password: password,
      osType,
      osVersion,
      app,
      appVersion,
      deviceId,
      installationId,
      appHash
    });
    store.dispatch({
      type: AUTH_SESSION,
      authSession
    });
    if (isLLU) {
      setCurrentPageIndex(PageIndexes.REGISTER_CHECK_EMAIL);
      if (!authSession.authentication_steps) {
        notifyApp('adc-webview:signin', authSession);
      }
      callback === null || callback === void 0 || callback();
      return;
    }
    if (values.country === 'US' && !isBypassPhoneNumberEnabled()) {
      var _authSession$verifica;
      const authUnverifiedTasks = (_authSession$verifica = authSession.verificationTasks) === null || _authSession$verifica === void 0 ? void 0 : _authSession$verifica.filter(task => task.verified === false);
      if (authUnverifiedTasks !== null && authUnverifiedTasks !== void 0 && authUnverifiedTasks.length) {
        const verificationTask = authUnverifiedTasks[0];
        mediator.publish('router:navigate', {
          url: '/verify-number',
          data: {
            taskID: verificationTask.id,
            mobileNumber: verificationTask.verificationTarget,
            onSuccessfulVerify: async () => {
              const updatedAuthSession = await signIn({
                email: email,
                password: password,
                osType,
                osVersion,
                app,
                appVersion,
                deviceId,
                installationId,
                appHash
              });
              store.dispatch({
                type: AUTH_SESSION,
                authSession: updatedAuthSession
              });
              mediator.publish('router:navigate', '/hipaa');
              callback === null || callback === void 0 || callback();
            }
          }
        });
      }
      return;
    }
    if (values.country === 'DK') {
      notifyApp('adc-webview:signin', authSession);
      return;
    }
    mediator.publish('router:navigate', '/hipaa');
    callback === null || callback === void 0 || callback();
  }, [app, appHash, appVersion, deviceId, installationId, isLLU, osType, osVersion, values.country]);
  const handleSubmitRegister = useCallback(register => {
    return new Promise((resolve, reject) => {
      setLoading(true);
      const newRegister = {
        ...register,
        ...values
      };
      registerPatient(newRegister, osType, osVersion, app, appVersion, phoneLanguage, appHash).then(() => notifyApp('adc-webview:create-account', {
        accountCreated: true
      })).then(async () => signInUser({
        email: newRegister.email,
        password: newRegister.password,
        callback: resolve,
        country: newRegister.country
      })).catch(error => {
        handleApiRequestError(error);
        reject(error);
      }).finally(() => {
        setLoading(false);
      });
    });
  }, [values, osType, osVersion, app, appVersion, phoneLanguage, appHash, signInUser, handleApiRequestError]);
  const {
    handleResendEmailButtonClick
  } = useResendVerificationEmail({
    account_id,
    osType,
    osVersion,
    app,
    appVersion,
    handleApiRequestError
  });
  const {
    handleContinueEmailVerificationButtonClick,
    isLoading
  } = useClickContinueEmailVerificationButton({
    account_id,
    osType,
    osVersion,
    app,
    appVersion,
    installationId,
    handleApiRequestError
  });
  const onNextPressed = useCallback(() => {
    switch (currentPageIndex) {
      case PageIndexes.REGISTER_CONFIRM_COUNTRY:
        setCurrentPageIndex(PageIndexes.REGISTER_ACCOUNT_FORM);
        break;
      case PageIndexes.REGISTER_ACCOUNT_FORM:
        {
          if (values.country === 'FR' && values.birthCountry === 'FR') {
            const isZipValid = validateFrenchZipCode(values.birthZipCode);
            if (!isZipValid) {
              setFieldError('birthZipCode', i18n.t('CreateAccountPersonalInfo.modals.personalInfoErrors.validZipCode'));
              return;
            }
          }
          const isMinor = validateIsMinor(values.dateOfBirth, minAge);
          const isNameValid = validateNameLength(values.firstName, values.lastName);
          if (!isNameValid) {
            return;
          }
          setValues({
            ...values,
            isMinor
          }, false);
          if (!isMinor) {
            setValues({
              ...values,
              password: '',
              confirmPassword: ''
            }, false);
            setTouched({
              ...touched,
              password: false,
              confirmPassword: false
            }, false);
            setCurrentPageIndex(PageIndexes.REGISTER_ADULT_ACCOUNT);
            return;
          }

          // no minors for 'llu'
          if (isLLU) {
            showMinorRestrictionMessage(minAge);
            return;
          }

          // user is minor, handle another flow: show minor related info
          setCurrentPageIndex(PageIndexes.REGISTER_MINOR_ACCOUNT_INTRO);
          break;
        }
      case PageIndexes.REGISTER_ADULT_ACCOUNT:
        handleSubmit();
        break;
      case PageIndexes.REGISTER_MINOR_ACCOUNT_INTRO:
        setCurrentPageIndex(PageIndexes.REGISTER_MINOR_ACCOUNT_INFO);
        break;
      case PageIndexes.REGISTER_MINOR_ACCOUNT_INFO:
        {
          const isNameValid = validateNameLength(values.guardianFirstName, values.guardianLastName);
          if (!isNameValid) {
            return;
          }
          setValues({
            ...values,
            password: '',
            confirmPassword: ''
          }, false);
          setTouched({
            ...touched,
            password: false,
            confirmPassword: false
          }, false);
          setCurrentPageIndex(PageIndexes.REGISTER_MINOR_ACCOUNT);
          break;
        }
      case PageIndexes.REGISTER_MINOR_ACCOUNT:
        handleSubmit();
        break;
      default:
        break;
    }
  }, [currentPageIndex, handleSubmit, values, validateIsMinor, minAge, validateNameLength, setValues, isLLU, setFieldError, setTouched, touched, showMinorRestrictionMessage]);
  const handleBackPressed = useCallback(() => {
    switch (currentPageIndex) {
      case PageIndexes.REGISTER_ACCOUNT_FORM:
        if (app !== 'llu') {
          notifyApp('adc-webview:navigate-back-app');
        } else {
          setCurrentPageIndex(PageIndexes.REGISTER_CONFIRM_COUNTRY);
        }
        break;
      case PageIndexes.REGISTER_ADULT_ACCOUNT:
        setCurrentPageIndex(PageIndexes.REGISTER_ACCOUNT_FORM);
        break;
      case PageIndexes.REGISTER_MINOR_ACCOUNT_INTRO:
        setCurrentPageIndex(PageIndexes.REGISTER_ACCOUNT_FORM);
        break;
      case PageIndexes.REGISTER_MINOR_ACCOUNT_INFO:
        setCurrentPageIndex(PageIndexes.REGISTER_MINOR_ACCOUNT_INTRO);
        break;
      case PageIndexes.REGISTER_MINOR_ACCOUNT:
        setCurrentPageIndex(PageIndexes.REGISTER_MINOR_ACCOUNT_INFO);
        break;
      case PageIndexes.REGISTER_CHECK_EMAIL:
        setCurrentPageIndex(PageIndexes.REGISTER_ADULT_ACCOUNT);
        break;
      default:
        notifyApp('adc-webview:navigate-back-app');
        break;
    }

    // fix for formik remembering state from before pressing BACK arrow
    setErrors({});
  }, [app, currentPageIndex, setErrors]);
  return /*#__PURE__*/React.createElement(MainContainer, {
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 587,
      columnNumber: 5
    }
  }, loading && /*#__PURE__*/React.createElement(LoadingModal, {
    "aria-label": "Loading",
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 588,
      columnNumber: 19
    }
  }), /*#__PURE__*/React.createElement(InformationalScreen, {
    topNavigationProps: {
      title: i18n.t('CreateAccountPersonalInfo.title'),
      animatedTitle: true,
      buttonStart: {
        element: /*#__PURE__*/React.createElement(TopHeaderBackArrow, {
          testID: "CreateAccount.backButton",
          __self: this,
          __source: {
            fileName: _jsxFileName,
            lineNumber: 594,
            columnNumber: 22
          }
        }),
        label: 'Back',
        onPress: handleBackPressed
      },
      testID: 'CreateAccount.topHeader'
    },
    index: currentPageIndex,
    testID: "CreateAccount",
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 589,
      columnNumber: 7
    }
  }, /*#__PURE__*/React.createElement(InformationalScreen.Page, {
    Content: /*#__PURE__*/React.createElement(RegisterConfirmCountry, {
      countries: supportedCountries,
      formikForm: formController,
      setLoading: setLoading,
      __self: this,
      __source: {
        fileName: _jsxFileName,
        lineNumber: 605,
        columnNumber: 13
      }
    }),
    Footer: /*#__PURE__*/React.createElement(Footer, {
      padding: "$4",
      buttonLabel: i18n.t('Global.microcopy.common.next'),
      onButtonPress: onNextPressed,
      buttonTestID: "RegisterConfirmCountry.nextButton",
      disabled: !values.country || !values.confirmCountryChecked || !isValid || isSubmitting,
      secondarySectionLabel: i18n.t('CreateAccountPersonalInfo.content.footerSignIn.primaryText'),
      onSecondaryButtonPress: handleClickSignIn,
      secondaryButtonTestID: "RegisterConfirmCountry.signIn",
      secondaryButtonLabel: i18n.t('CreateAccountPersonalInfo.content.footerSignIn.links.signIn.label'),
      __self: this,
      __source: {
        fileName: _jsxFileName,
        lineNumber: 612,
        columnNumber: 13
      }
    }),
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 603,
      columnNumber: 9
    }
  }), /*#__PURE__*/React.createElement(InformationalScreen.Page, {
    Content: /*#__PURE__*/React.createElement(RegisterAccountForm, {
      isLLU: isLLU,
      countries: countries,
      formikForm: formController,
      __self: this,
      __source: {
        fileName: _jsxFileName,
        lineNumber: 634,
        columnNumber: 13
      }
    }),
    Footer: /*#__PURE__*/React.createElement(Footer, {
      padding: "$4",
      buttonLabel: i18n.t('CreateAccountPersonalInfo.content.createAccountPersonalInfoForm.links.next.label'),
      onButtonPress: onNextPressed,
      buttonTestID: "RegisterPersonalInfo.nextButton",
      secondarySectionLabel: i18n.t('CreateAccountPersonalInfo.content.footerSignIn.primaryText'),
      onSecondaryButtonPress: handleClickSignIn,
      secondaryButtonTestID: "RegisterPersonalInfo.signIn",
      secondaryButtonLabel: i18n.t('CreateAccountPersonalInfo.content.footerSignIn.links.signIn.label'),
      disabled: !values.firstName || !values.lastName || !values.dateOfBirth || !isValid || isSubmitting,
      __self: this,
      __source: {
        fileName: _jsxFileName,
        lineNumber: 637,
        columnNumber: 13
      }
    }),
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 632,
      columnNumber: 9
    }
  }), /*#__PURE__*/React.createElement(InformationalScreen.Page, {
    Content: /*#__PURE__*/React.createElement(RegisterAdultAccount, {
      showPhoneInput: showPhoneInput,
      formikForm: formController,
      passwordRules: passwordRules,
      onValidate: setMeetsPasswordRequirements,
      __self: this,
      __source: {
        fileName: _jsxFileName,
        lineNumber: 665,
        columnNumber: 13
      }
    }),
    Footer: /*#__PURE__*/React.createElement(Footer, {
      padding: "$4",
      buttonLabel: i18n.t('Global.microcopy.common.createAccount'),
      onButtonPress: handleSubmit,
      buttonTestID: "RegisterAccountInfo.nextButton",
      secondarySectionLabel: i18n.t('CreateAccountAccountInfo.content.footerSignIn.primaryText'),
      onSecondaryButtonPress: handleClickSignIn,
      secondaryButtonTestID: "RegisterAccountInfo.signIn",
      secondaryButtonLabel: i18n.t('CreateAccountAccountInfo.content.footerSignIn.links.signIn.label'),
      disabled: !values.email || !values.password || !values.confirmPassword || !isValid || isSubmitting || !meetsPasswordRequirements || !isLLU && values.country === 'US' && !values.mobileNumber,
      __self: this,
      __source: {
        fileName: _jsxFileName,
        lineNumber: 673,
        columnNumber: 13
      }
    }),
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 663,
      columnNumber: 9
    }
  }), /*#__PURE__*/React.createElement(InformationalScreen.Page, {
    Content: RegisterMinorAccountIntro,
    Footer: /*#__PURE__*/React.createElement(Footer, {
      padding: "$4",
      buttonLabel: i18n.t('Global.microcopy.common.continue'),
      onButtonPress: () => onNextPressed(),
      buttonTestID: "CreateAccountIntro.continue-button",
      disabled: false,
      __self: this,
      __source: {
        fileName: _jsxFileName,
        lineNumber: 702,
        columnNumber: 13
      }
    }),
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 699,
      columnNumber: 9
    }
  }), /*#__PURE__*/React.createElement(InformationalScreen.Page, {
    Content: /*#__PURE__*/React.createElement(RegisterMinorAccountInfo, {
      formikForm: formController,
      __self: this,
      __source: {
        fileName: _jsxFileName,
        lineNumber: 713,
        columnNumber: 20
      }
    }),
    Footer: /*#__PURE__*/React.createElement(Footer, {
      padding: "$4",
      buttonLabel: i18n.t('CreateAccountPersonalInfo.content.createAccountPersonalInfoForm.links.next.label'),
      onButtonPress: () => onNextPressed(),
      buttonTestID: "CreateAccountPersonalInfo.nextButton",
      disabled: !values.guardianFirstName || !values.guardianLastName || !isValid || isSubmitting,
      __self: this,
      __source: {
        fileName: _jsxFileName,
        lineNumber: 715,
        columnNumber: 13
      }
    }),
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 712,
      columnNumber: 9
    }
  }), /*#__PURE__*/React.createElement(InformationalScreen.Page, {
    Content: /*#__PURE__*/React.createElement(RegisterMinorAccount, {
      showPhoneInput: showPhoneInput,
      formController: formController,
      passwordRules: passwordRules,
      onValidate: setMeetsPasswordRequirements,
      __self: this,
      __source: {
        fileName: _jsxFileName,
        lineNumber: 731,
        columnNumber: 13
      }
    }),
    Footer: /*#__PURE__*/React.createElement(Footer, {
      padding: "$4",
      buttonLabel: i18n.t('Global.microcopy.common.createAccount'),
      onButtonPress: handleSubmit,
      buttonTestID: "CreateAccountAccountInfo.nextButton",
      disabled: !values.email || !values.password || !values.confirmPassword || !isLLU && values.country === 'US' && !values.mobileNumber || !isValid || isSubmitting || !meetsPasswordRequirements,
      __self: this,
      __source: {
        fileName: _jsxFileName,
        lineNumber: 739,
        columnNumber: 13
      }
    }),
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 729,
      columnNumber: 9
    }
  }), /*#__PURE__*/React.createElement(InformationalScreen.Page, {
    Content: /*#__PURE__*/React.createElement(ContentContainer, {
      __self: this,
      __source: {
        fileName: _jsxFileName,
        lineNumber: 758,
        columnNumber: 13
      }
    }, /*#__PURE__*/React.createElement(CheckYourEmailComponents.Content, {
      email: formController === null || formController === void 0 || (_formController$value = formController.values) === null || _formController$value === void 0 ? void 0 : _formController$value.email,
      onButtonPress: handleResendEmailButtonClick,
      __self: this,
      __source: {
        fileName: _jsxFileName,
        lineNumber: 759,
        columnNumber: 15
      }
    })),
    Footer: /*#__PURE__*/React.createElement(Footer, {
      buttonLabel: i18n.t('Global.microcopy.common.continue'),
      onButtonPress: handleContinueEmailVerificationButtonClick,
      padding: "$4",
      buttonTestID: "CheckYourEmailRegistration.continueButton",
      disabled: isLoading,
      __self: this,
      __source: {
        fileName: _jsxFileName,
        lineNumber: 766,
        columnNumber: 13
      }
    }),
    topNavigationProps: {
      title: i18n.t('CheckYourEmailRegistration.title'),
      animatedTitle: true,
      testID: 'CreateAccount.topHeader',
      buttonStart: {
        element: /*#__PURE__*/React.createElement(TopHeaderBackArrow, {
          testID: "CreateAccount.backButton",
          __self: this,
          __source: {
            fileName: _jsxFileName,
            lineNumber: 779,
            columnNumber: 24
          }
        }),
        onPress: handleBackPressed
      }
    },
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 756,
      columnNumber: 9
    }
  })));
};
export default connector(CreateNewUser);